import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from "react";
import {
  InputAdornment,
  InputLabel,
  makeStyles,
  Paper,
  TextField,
} from "@material-ui/core";
import { Search as SearchIcon } from "@material-ui/icons";
import { debounce } from "lodash";

import theme from "theme";

const useStyles = makeStyles(theme => ({
  base: {
    borderRadius: theme.custom.shape.borderRadiusSoft,
  },
  textField: {
    borderRadius: theme.custom.shape.borderRadiusSoft,
    backgroundColor: theme.palette.background.default,
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        border: "unset",
      },
      "&:hover fieldset": {
        border: "unset",
      },
      "&.Mui-focused fieldset": {
        border: "unset",
      },
    },
  },
}));

interface SearchFieldProps {
  activeQuery: string;
  debounceTime: number;
  fieldId: string;
  onChangeCommitted: (query: string) => void;
  placeholder: string;
}

const SearchField: FC<SearchFieldProps> = ({
  activeQuery,
  debounceTime,
  fieldId,
  onChangeCommitted,
  placeholder,
}) => {
  const styles = useStyles(theme);
  const [query, setQuery] = useState(activeQuery);
  const [isChangeCommitted, setIsChangeCommitted] = useState(true);

  // Use ref to always have the latest callback without recreating debounced function
  const onChangeCommittedRef = useRef(onChangeCommitted);
  useEffect(() => {
    onChangeCommittedRef.current = onChangeCommitted;
  }, [onChangeCommitted]);

  // Add an optional cancel method to the type, if we receive a debounce time and call debounce, lodash adds a cancel method to the function
  const debouncedOnChange: ((query: string) => void) & {
    cancel?: () => void;
  } = useMemo(() => {
    const debouncedFn = debounceTime
      ? debounce((query: string) => {
          onChangeCommittedRef.current(query);
          setIsChangeCommitted(true);
        }, debounceTime)
      : (query: string) => {
          onChangeCommittedRef.current(query);
          setIsChangeCommitted(true);
        };

    return debouncedFn;
  }, [debounceTime]);

  // Cleanup: cancel pending debounced calls when component unmounts
  useEffect(() => {
    return () => {
      debouncedOnChange.cancel?.();
    };
  }, [debouncedOnChange]);

  // Sync local state with external activeQuery when it changes and we're not typing
  useEffect(() => {
    if (query !== activeQuery && isChangeCommitted) {
      setQuery(activeQuery);
    }
    // Intentionally NOT including 'query' to avoid re-triggering when local state updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuery, isChangeCommitted]);

  return (
    <Paper className={styles.base} elevation={3} style={{ width: "100%" }}>
      <InputLabel htmlFor={fieldId} style={{ display: "none" }}>
        Search Listings
      </InputLabel>
      <TextField
        id={fieldId}
        placeholder={placeholder}
        helperText=""
        variant="outlined"
        value={query}
        onChange={(ev: ChangeEvent<HTMLInputElement>) => {
          setIsChangeCommitted(false);
          debouncedOnChange(ev.target.value);
          setQuery(ev.target.value);
        }}
        fullWidth
        size="small"
        className={styles.textField}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
    </Paper>
  );
};

export default SearchField;
